import { MatSliderChange } from '@angular/material/slider';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { EditingDevice, WidgetComponentType } from '../../campaign/widgets.model';

@Component({
  selector: 'dimension-picker',
  templateUrl: './dimension-picker.component.html',
  styleUrls: ['./dimension-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DimensionPickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public checkboxDisabled: boolean;
  @Input() public isEmbeddedComp: boolean;
  @Input() public dimensionType: DimensionType;
  @Input() public component;
  @Input() public brandSettingsValue: string;
  @Input() public typeControl: string;
  @Input() public disabled: string;
  @Input() public isOnlySlider = false;
  @Input() public isV2 = false;
  @Input() public v2Max: number;
  @Input() public minValue = 0;
  @Input() public showEditingDevice = false;
  @Input() public disableMobileEditingDevice = false;
  @Input() public pixelAllowed: boolean;
  @Input() public percentAllowed: boolean;
  @Input() public sliderDisabled: boolean;
  @Input() public label: string;
  @Input() public defaultValue: number;
  @Input() public widthShouldBeUnchecked: boolean;
  @Input() public percentSliderAllowed = false;

  @Input() public set value(data: any) {
    this._value = typeof data === 'object' ? data?.value : data;
    this.brandSettingsValue = data?.brandSettingsValue;
    this.isSet = this.isV2 ? true : this.isCheckBoxSet;
    if (this.isSet || this.isV2) {
      this.currentValue = this.isBrandSettingsValueApplied
        ? WidgetComponentHelpers.extractNumberFromPixelOrPercentString(this.brandSettingsValue)
        : WidgetComponentHelpers.extractNumberFromPixelOrPercentString(this._value);
    }
  }

  @Input() public set range(val: number) {
    this._range = val || 150;
  }

  @Input() public set currentEditingDevice(currentEditingDevice: EditingDevice) {
    this._currentEditingDevice = currentEditingDevice;
  }
  public get currentEditingDevice(): EditingDevice {
    return this._currentEditingDevice;
  }

  @Output() public valueChanged = new EventEmitter<string>();
  @Output() public onCheckboxChanged = new EventEmitter<boolean>();
  @Output() public onEditingDeviceChanged = new EventEmitter<EditingDevice>();

  public DimensionType = DimensionType;
  public WidgetComponentType = WidgetComponentType;
  public EditingDevices = EditingDevice;

  private _value: string;
  public currentValue: number;
  public currentUnit: string;
  public isInvalid = false;
  public isSet: boolean;

  public sliderVal: number;
  public sliderMin: number;
  public sliderMax: number;
  public pastValue: number;
  public interval: any = false;

  public _currentEditingDevice = undefined as EditingDevice;
  private _range = 150;

  public get isCheckBoxSet() {
    if (this.brandSettingsValue) {
      return (this._value !== '0px' && !!this._value) || this.isBrandSettingsValueApplied;
    } else return !!this._value;
  }

  public get isBrandSettingsValueApplied() {
    return this.brandSettingsValue && this._value === null;
  }

  public get isPercentSlider(): boolean {
    return this.percentSliderAllowed && this.percentAllowed && this.currentUnit === '%';
  }

  public get v2SliderMax(): number {
    if (this.currentUnit === '%') {
      return 100;
    }
    if (this.v2Max) {
      return this.v2Max;
    }
    return Math.max(this.currentValue * 2 || 500, 500);
  }

  public get defaultValueWhenUnCheked() {
    switch (this.component?.type) {
      case WidgetComponentType.Button:
      case WidgetComponentType.InputText:
        return null;
      default:
        return '20px';
    }
  }

  public ngOnInit(): void {
    this.resetSlider();
    this.setDefaultHeightForEmbedded();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.resetSlider();
    }
    // Recompute once every @Input for this round has landed, so the result doesn't depend on attribute order.
    if (
      changes.value ||
      changes.pixelAllowed ||
      changes.percentAllowed ||
      changes.widthShouldBeUnchecked ||
      changes.dimensionType ||
      changes.isEmbeddedComp
    ) {
      this.initializeCurrentUnit();
      this.setDefaultHeightForEmbedded();
      // resetSlider above ran before the unit was known, and the slider range depends on it.
      this.updateRange();
    }
  }

  public updateEditingDevice() {
    this.onEditingDeviceChanged.emit(this.currentEditingDevice);
  }

  public toggleCheckbox() {
    this.currentUnit = this.defaultUnit;
    if (this.isSet) {
      this.currentValue = this.defaultValue || this.currentValue;
      this._value = this.currentValue + this.currentUnit;
      if (this.brandSettingsValue) this.applyBrandSettings();
    } else {
      if (this.brandSettingsValue) {
        this.currentValue = 0;
        this._value = '0px';
      } else {
        if (this.dimensionType === DimensionType.Height) {
          this.currentValue = WidgetComponentHelpers.extractNumberFromPixelOrPercentString(
            this.defaultValueWhenUnCheked,
          );
        } else this.currentValue = undefined;
        this._value = undefined;
        this.resetSlider();
      }
    }
    this.onCheckboxChanged.emit(this.isSet);
    this.valueChanged.emit(this._value);
  }

  public inputData(e): void {
    if (e.target.value < this.minValue || (e.target.value > 100 && this.currentUnit === '%')) {
      this.isInvalid = true;
    }
  }

  public updateSlider(e: MatSliderChange) {
    if (e.value < 0) {
      this.currentValue = 0;
      return;
    }
    if (this.isPercentSlider) {
      // % has a fixed 0-100 range, so the px slider's sliding window isn't needed.
      this.currentValue = e.value;
      this.sliderVal = this.currentValue;
      this.updateWidget();
      return;
    }
    const diff = e.value - this.pastValue;
    const dir = diff / Math.abs(diff); // -1 or 1 dependnig on if slider is to the left or right of the center
    if (e.value < this.pastValue + this._range - 2 && e.value > this.pastValue - this._range + 2) {
      this.currentValue = e.value;
      this.sliderVal = this.currentValue;
      this.updateWidget();
      if (!this.interval) return;
      clearInterval(this.interval);
      this.interval = false;
    } else if (!this.interval) {
      this.interval = setInterval(() => {
        this.currentValue += 3 * dir;
        if (this.currentValue <= 0) {
          this.currentValue = 0;
          return;
        }
        this.pastValue += 3 * dir;
        this.updateRange();
        this.sliderVal = this.pastValue + dir * this._range;
        this.updateWidget();
      }, 50);
    }
  }

  public resetSlider() {
    if (isNaN(this.currentValue) && !this.isEmbeddedComp && this.dimensionType !== DimensionType.MinWidth) {
      this.currentValue = this.defaultValue || 20;
    }
    this.pastValue = this.currentValue;
    this.sliderVal = this.currentValue;
    this.updateRange();
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = false;
  }

  public onChange() {
    this.resetSlider();
    this.updateWidget();
  }

  public applyBrandSettings(): void {
    this._value = null;
    this.valueChanged.emit(this._value);
  }

  private initializeCurrentUnit() {
    this.isSet = !this.widthShouldBeUnchecked && this.isCheckBoxSet;
    if (this._value || this.isBrandSettingsValueApplied) {
      this.currentUnit = !this.isBrandSettingsValueApplied
        ? WidgetComponentHelpers.extractDimensionUnit(this._value)
        : 'px';
      if ((this.currentUnit === '%' && !this.percentAllowed) || (this.currentUnit === 'px' && !this.pixelAllowed)) {
        this.isSet = false;
      }
    } else {
      this.currentUnit = this.defaultUnit;
    }
  }

  private get defaultUnit(): string {
    // Height has always defaulted to px; allowing % for it must not change that.
    const preferPixel = this.dimensionType === DimensionType.Height;
    if (preferPixel && this.pixelAllowed === true) {
      return 'px';
    }
    if (this.percentAllowed === true) {
      return '%';
    }
    if (this.pixelAllowed === true) {
      return 'px';
    }
    return undefined;
  }

  private updateRange() {
    this.sliderMin = 0;
    this.sliderMax = this.isPercentSlider ? 100 : Number(this.pastValue) + Number(this._range);
  }

  private updateWidget() {
    if (this.currentValue < this.minValue && this.currentUnit === 'px') {
      this.currentValue = this.minValue;
    }
    if (this.currentValue > 100 && this.currentUnit === '%') {
      this.currentValue = 100;
    }
    this.isInvalid = false;
    if (!this.isOnlySlider) {
      this._value = this.currentValue !== null ? this.currentValue + this.currentUnit : null;
    } else {
      this._value = this.currentValue + '';
    }
    this.valueChanged.emit(this._value);
  }

  private setDefaultHeightForEmbedded() {
    if (this.isEmbeddedComp && !this.isSet && this.dimensionType === DimensionType.Height) {
      this.currentValue = 20;
    }
    if (this.isEmbeddedComp && !this.isSet && this.dimensionType === DimensionType.MinWidth) {
      this.currentValue = null;
      this.isSet = true;
    }
  }

  public v2SliderInput(value: number): void {
    this.currentValue = this.clampV2(value);
    this.updateWidget();
  }

  public v2InputChange(event: Event): void {
    const val = +(event.target as HTMLInputElement).value;
    this.currentValue = this.clampV2(val);
    this.updateWidget();
  }

  public v2ToggleBrand(): void {
    if (this.isBrandSettingsValueApplied) {
      this._value = this.currentValue + this.currentUnit;
      this.valueChanged.emit(this._value);
    } else {
      this.applyBrandSettings();
    }
  }

  public v2UnitChange(unit: string): void {
    this.currentUnit = unit;
    this.currentValue = this.clampV2(this.currentValue);
    this.updateWidget();
  }

  private clampV2(value: number): number {
    if (value < this.minValue) {
      return this.minValue;
    }
    if (this.currentUnit === '%' && value > 100) {
      return 100;
    }
    if (this.v2Max && value > this.v2Max) {
      return this.v2Max;
    }
    return value;
  }

  public ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

export enum DimensionType {
  MinWidth = 'Min width',
  MaxWidth = 'Max width',
  Height = 'Height',
  Width = 'Width',
}
